import { CancelToken } from "axios";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { compose } from "redux";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import {
  AccountType,
  ActionList,
  DropdownInput,
  IIconTextLinkProps,
  IOption,
  PageHeader,
  Search,
  Head,
  PerformerAppSmartBanner,
  PerformerAppPath,
} from "component-library";
import { navigator } from "shared/services/navigator";
import scrollManager from "shared/services/scrollManager";
import { MapDispatchToProps } from "shared/types/utilities";

import { PageTitleKey } from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IJobFilterParams } from "models/api";
import { IActionList, IActionListItem } from "models/common";
import { IJobItem, IPaginatedJobItemList } from "models/job";

import copyPrefixedObjectKeys from "helpers/copyPrefixedObjectKeys";
import { getPageTitle } from "helpers/getPageTitle";
import jobService, { SetJobActionType } from "services/api/job/jobService";
import applicationMap from "services/applicationMap";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";

import withJobsFilter, {
  IWithJobsFilterOptions,
  IWithJobsFilterProps,
} from "hoc/withJobsFilter/withJobsFilter";
import {
  FilterType,
  IJobSender,
  ShowType,
  SortingType,
} from "hoc/withJobsFilter/withJobsFilter.config";

import { JobsPageTab, SEARCH_MAX_LENGTH } from "pages/JobsPage/JobsPage.config";
import { JobItemToJobTableItemConverter } from "pages/JobsPage/shared/shared.converters";
import { prepareJobItemToJobTableItemConverterTexts } from "pages/JobsPage/shared/shared.helpers";

import JobsTable, { IJobsTableTexts } from "components/JobsTable/JobsTable";
import { prepareTableTexts } from "components/JobsTable/JobsTable.helpers";
import {
  IJobTableItem,
  IJobTableItemTag,
  JobTableItemUuid,
} from "components/JobsTable/JobsTable.models";

import { IJobsPageInjectedProps, IJobsPageMatchParams } from "../JobsPage";

import {
  ActionListItemKey,
  prepareActionList,
} from "./MatchingMyFilterPage.helpers";
import {
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";

export interface IMatchingMyFilterPageTexts {
  table: IJobsTableTexts;
  pageTitle: string;
}

export interface IMatchingMyFilterPageStoreProps {
  actionList: IActionList;
  texts: IMatchingMyFilterPageTexts;
  convertToTableItem: (
    jobItem: IJobItem,
    ldFlags?: LDFlagSet,
    userType?: AccountType
  ) => IJobTableItem;
  findJobs(
    params: IJobFilterParams,
    cancellationToken?: CancelToken
  ): Promise<IPaginatedJobItemList>;
  markAsRead: SetJobActionType;
  markAsUnread: SetJobActionType;
  isPerformer?: boolean;
}

export interface IMatchingMyFilterPageActions {
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export interface IMatchingMyFilterPageProps
  extends IMatchingMyFilterPageStoreProps,
    IJobsPageInjectedProps,
    RouteComponentProps<IJobsPageMatchParams>,
    IMatchingMyFilterPageActions,
    IWithJobsFilterProps,
    LaunchDarklyClientWithFlags {}

export interface IMatchingMyFilterPageState {
  selectedItems: JobTableItemUuid[];
}

export const INPUT_NAMES = {
  showType: "showType",
  sortingType: "sortingType",
};

export class MatchingMyFilterPage extends React.Component<
  IMatchingMyFilterPageProps,
  IMatchingMyFilterPageState
> {
  public static pageSize: number = 50;
  public readonly state: Readonly<IMatchingMyFilterPageState> = {
    selectedItems: [],
  };

  private readonly searchRef = React.createRef<Search>();

  private readonly actionListHandlers: {
    [ActionListItemKey.ChangeSettings](
      e: React.SyntheticEvent<HTMLAnchorElement>
    ): void;
  };

  constructor(props: IMatchingMyFilterPageProps) {
    super(props);

    const actionListHandlers = {
      [ActionListItemKey.ChangeSettings]: this.handleChangeSettingsClick,
    };
    this.actionListHandlers = copyPrefixedObjectKeys(
      actionListHandlers,
      "more-dropdown_"
    );
  }

  public componentDidMount() {
    this.initSearch();
  }

  public render() {
    const {
      filterValues,
      paginatedItems,
      isLoading,
      commonTexts,
      actionList,
      texts,
      renderJobsFilterControl,
      isPerformer,
    } = this.props;
    const { selectedItems } = this.state;

    return (
      <section>
        <Head registerPageView={this.registerPageView}>
          <title>{texts.pageTitle}</title>
        </Head>
        <PerformerAppSmartBanner appPath={PerformerAppPath.MyJobs} />
        <PageHeader
          pageTitle={commonTexts.title}
          bottomSection={this.renderBottomSection()}
          actionSection={
            <ActionList
              variant="pageHeader"
              items={this.prepareActionListItems(actionList.items)}
              moreButtonText={actionList.moreButtonText}
            />
          }
        />
        {renderJobsFilterControl()}
        <div className="g-content-area">
          <div className="g-col-lg-12 g-table-wrapper">
            <JobsTable
              texts={texts.table}
              items={paginatedItems.items}
              selectedItems={selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onSenderClick={this.handleSenderClick}
              onTagClick={this.handleTagClick}
              itemsPerPage={MatchingMyFilterPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={filterValues[FilterType.PageNumber]}
              itemsCount={paginatedItems.count}
              updatePageNumber={this.handleUpdatePageNumber}
              onMarkAsRead={this.handleMarkAsRead}
              onMarkAsUnread={this.handleMarkAsUnread}
              onRefresh={this.handleRefresh}
              returnUri={applicationMap.links.jobsMyFilters()}
              isPerformer={isPerformer}
            />
          </div>
        </div>
      </section>
    );
  }

  private renderBottomSection = () => {
    const { commonTexts, filterValues } = this.props;

    return (
      <div className="g-inner-grid-12 g-row-gap-sm g-column-gap-sm">
        <div className="g-col-lg-6">
          <Search
            ref={this.searchRef}
            placeholder={commonTexts.searchPlaceholder}
            maxLength={SEARCH_MAX_LENGTH}
            onSearch={this.handleSearch}
            onClear={this.handleSearchClear}
          />
        </div>
        <div className="g-col-lg-3 g-col-sm-6">
          <DropdownInput<string>
            id="filtersShowType"
            name={INPUT_NAMES.showType}
            variant="select"
            options={commonTexts.filterOptions}
            selected={commonTexts.filterOptions.find(
              (option) => option.value === filterValues[FilterType.ShowType]
            )}
            value={filterValues[FilterType.ShowType]}
            optionSelected={this.handleShowTypeChange}
            notTruncateOptionsOnSmall={true}
          />
        </div>
        <div className="g-col-lg-3 g-col-sm-6">
          <DropdownInput<string>
            id="filtersSortingType"
            name={INPUT_NAMES.sortingType}
            variant="select"
            options={commonTexts.sortingOptions}
            selected={commonTexts.sortingOptions.find(
              (option) => option.value === filterValues[FilterType.SortingType]
            )}
            value={filterValues[FilterType.SortingType]}
            optionSelected={this.handleSortingTypeChange}
            notTruncateOptionsOnSmall={true}
          />
        </div>
      </div>
    );
  };

  private handleChangeSettingsClick = (
    e: React.SyntheticEvent<HTMLAnchorElement>
  ) => {
    window.location.assign(e.currentTarget.href);
  };

  private handleUpdatePageNumber = (page: number) => {
    navigator.setQuery({ page });
  };

  private handleShowTypeChange = (option: IOption<ShowType>) => {
    this.props.changeFilters({ [FilterType.ShowType]: option.value });
  };

  private handleSortingTypeChange = (option: IOption<SortingType>) => {
    this.props.changeFilters({ [FilterType.SortingType]: option.value });
  };

  private handleSenderClick = (sender: IJobSender) => {
    scrollManager.reset();
    this.props.changeFilters({ [FilterType.Sender]: sender });
  };

  private handleTagClick = (tag: IJobTableItemTag) => {
    scrollManager.reset();
    this.props.addFilterValue(tag.filterType, tag.facetId);
  };

  private handleSearch = (search: string | undefined) => {
    this.deselectAllItems();
    this.props.changeFilters({ [FilterType.TextFilter]: search });
  };

  private handleSearchClear = () => {
    this.handleSearch(undefined);
  };

  private createHandlerActionListItemClick =
    (id: string = "") =>
    (e: React.MouseEvent) => {
      e.preventDefault();

      this.actionListHandlers[id]?.(e);
    };

  private prepareActionListItems(
    items: IActionListItem[]
  ): IIconTextLinkProps[] {
    return items?.map(
      (item: IActionListItem): IIconTextLinkProps => ({
        ...item,
        handleClick: this.createHandlerActionListItemClick(item.id),
      })
    );
  }

  private selectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({ selectedItems: [...this.state.selectedItems, ...itemIds] });
  };

  private deselectItems = (itemIds: JobTableItemUuid[]) => {
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !itemIds.includes(item)
      ),
    });
  };

  private deselectAllItems = () => {
    this.setState({ selectedItems: [] });
  };

  private handleMarkAsRead = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsRead(itemIds);
    this.deselectAllItems();
  };

  private handleMarkAsUnread = async (itemIds: JobTableItemUuid[]) => {
    await this.props.onMarkAsUnread(itemIds);
    this.deselectAllItems();
  };

  private handleRefresh = () => {
    this.props.changeFilters({ [FilterType.PageNumber]: 1 });
    this.props.refreshCounters();
  };

  private initSearch() {
    const searchString = this.props.filterValues[FilterType.TextFilter];
    this.searchRef.current?.setState({ searchString: searchString ?? "" });
  }

  private registerPageView = () => {
    GTMEvents.jobsFeed.pageViews.matchingMyFilters(this.props.texts.pageTitle);
  };
}

const mapStateToProps = (
  state: IApplicationState,
  ownProps: IJobsPageInjectedProps
): IMatchingMyFilterPageStoreProps => {
  const pageTranslations = state.translation.pages[PageKey.Jobs]!;
  const commonTranslations = state.translation.common;
  const userType = state.user.data!.type;

  const convertToTableItem = new JobItemToJobTableItemConverter(
    prepareJobItemToJobTableItemConverterTexts(
      pageTranslations,
      commonTranslations
    ),
    ownProps.productionTypes,
    ownProps.contractTypes
  ).convert;

  return {
    actionList: prepareActionList(pageTranslations),
    texts: {
      table: prepareTableTexts(commonTranslations),
      pageTitle: getPageTitle(
        commonTranslations,
        PageTitleKey.JobsFeedMatchingMySettings
      ),
    },
    findJobs: (...args) => jobService.find(...args),
    markAsRead: (...args) => jobService.markAsRead(...args),
    markAsUnread: (...args) => jobService.markAsUnread(...args),
    convertToTableItem,
    isPerformer: userType === AccountType.Performer,
  };
};

const mapDispatchToProps: MapDispatchToProps<IMatchingMyFilterPageActions> = {
  setLoading,
  showPopup,
  hidePopup,
};

export const withJobsFilterOptions: IWithJobsFilterOptions = {
  tab: JobsPageTab.MyFilter,
  applyUserSettings: true,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLaunchDarklyConsumerHOC(),
  withJobsFilter<IMatchingMyFilterPageProps>(withJobsFilterOptions)
)(MatchingMyFilterPage);
